import apiClient, { safeJson } from '@/api/client';
import {
    SettlementCategoriesResponse,
    SettlementCategoriesState,
    SettlementCategoryMutationResponse,
} from './types';

/**
 * @method get
 * @brief 센터 정산 분류 목록(보관 포함, 만든 순서)
 *
 * 구서버에는 이 API 가 없다(404). 그때는 throw 하지 않고 available=false 를 돌려
 * 화면이 새 UI 를 숨기게 한다 — 키 없음 = 기능 미배포.
 */
/**
 * 정산 분류 기능의 요청은 모두 401 자동 갱신·재전송을 끈다(noAuthRetry, 공용 클라이언트 요청별 옵션).
 * 다른 탭에서 새로 로그인한 토큰을 이 탭의 옛 갱신 토큰으로 덮어쓰지 않게 한다. 401 이면 그대로 실패한다.
 */
export const SETTLEMENT_CLASSIFICATION_REQUEST_OPTIONS = { noAuthRetry: true } as const;

export const getSettlementCategories = async (centerId: string, signal?: AbortSignal): Promise<SettlementCategoriesState> => {
    let res: Response;
    try {
        res = await apiClient.get(`v1/settlement-classification/center/${centerId}/categories`, {
            throwHttpErrors: false,
            skipErrorAlertFor: [404],
            ...SETTLEMENT_CLASSIFICATION_REQUEST_OPTIONS,
            ...(signal ? { signal } : {}),
        } as any);
    } catch (error) {
        // 토큰 갱신 후 재요청은 ky 기본 옵션으로 나가 404 도 throw 된다. 같은 계약을 지킨다.
        if ((error as { status?: number })?.status === 404 || (error as any)?.response?.status === 404) {
            return { available: false, categories: [] };
        }
        throw error;
    }
    if (res.status === 404) return { available: false, categories: [] };
    if (!res.ok) throw Object.assign(new Error('정산 분류를 불러오지 못했어요.'), { status: res.status });
    const data = (await safeJson(res)) as SettlementCategoriesResponse | null;
    if (!data || !Array.isArray(data.categories)) return { available: false, categories: [] };
    return { available: true, categories: data.categories };
};

/** @method post @brief 정산 분류 추가 */
export const postSettlementCategory = async ({ centerId, name }: { centerId: string; name: string }) => {
    const res = await apiClient.post(`v1/settlement-classification/center/${centerId}/categories`, {
        json: { name },
        ...SETTLEMENT_CLASSIFICATION_REQUEST_OPTIONS,
    });
    return (await safeJson(res)) as SettlementCategoryMutationResponse | null;
};

/** @method patch @brief 정산 분류 이름 변경·보관·되살리기 */
export const patchSettlementCategory = async ({
    centerId,
    key,
    body,
}: {
    centerId: string;
    key: string;
    body: { name?: string; archived?: boolean };
}) => {
    const res = await apiClient.patch(
        `v1/settlement-classification/center/${centerId}/categories/${encodeURIComponent(key)}`,
        { json: body, ...SETTLEMENT_CLASSIFICATION_REQUEST_OPTIONS },
    );
    return (await safeJson(res)) as SettlementCategoryMutationResponse | null;
};

export interface CorrectMembershipSettlementBody {
    settlementIssueKind?: 'new' | 'reRegistration' | null;
    settlementCategoryKey?: string | null;
    reason?: string;
}

export interface CorrectMembershipSettlementResponse {
    rootMembershipId: string;
    updatedMembershipIds: string[];
    unchangedMembershipIds: string[];
    skippedMembershipIds: string[];
}

/**
 * @method patch
 * @brief 수강권 정산 기준 정정(관리자 전용). 지정한 수강권과 강사 인계로 이어진 하위 세대를 함께 고친다.
 * 고친 값만 보낸다(둘 다 없으면 서버 400).
 */
export const patchMembershipSettlementBasis = async ({
    centerId,
    membershipId,
    body,
}: {
    centerId: string;
    membershipId: string;
    body: CorrectMembershipSettlementBody;
}) => {
    const res = await apiClient.patch(
        `v1/settlement-classification/center/${centerId}/memberships/${encodeURIComponent(membershipId)}`,
        { json: body, ...SETTLEMENT_CLASSIFICATION_REQUEST_OPTIONS },
    );
    return (await safeJson(res)) as CorrectMembershipSettlementResponse | null;
};
